import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { ManageSurveyActions } from '@ps/survey-data-access';
import { concatMap, map, of, switchMap, tap } from 'rxjs';

import {
  SurveyManagementActions,
  SurveyManagementApiActions,
} from './survey-management.actions';
import { SurveyManagementService } from '../services/survey-management.service';
import { FusionService } from '../services/fusion.service';

@Injectable()
export class SurveyManagementEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _service = inject(SurveyManagementService);
  private readonly _fusionService = inject(FusionService);
  private readonly _toast = inject(ToastService);

  public assignBuyerProjectManager$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementActions.assignBuyerProjectManager),
      switchMap(({ surveyId, projectManager }) =>
        this._service
          .assignBuyerProjectManager(surveyId, projectManager.id)
          .pipe(
            tap(() =>
              this._toast.success(
                'Success! Buyer Project Manager assigned successfully',
                { keepOpen: false },
              ),
            ),
            map(() =>
              SurveyManagementApiActions.assignBuyerProjectManagerSuccess({
                surveyId,
                projectManager,
              }),
            ),
            this.psCatchError(
              (error) =>
                of(
                  SurveyManagementApiActions.assignBuyerProjectManagerFailure({
                    error: error.message,
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public removeBuyerProjectManager$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementActions.removeBuyerProjectManager),
      switchMap(({ surveyId }) =>
        this._service.removeBuyerProjectManager(surveyId).pipe(
          tap(() =>
            this._toast.success(
              'Success! Buyer Project Manager removed successfully',
              { keepOpen: false },
            ),
          ),
          map(() =>
            SurveyManagementApiActions.removeBuyerProjectManagerSuccess({
              surveyId,
            }),
          ),
          this.psCatchError(
            (error) =>
              of(
                SurveyManagementApiActions.removeBuyerProjectManagerFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public assignOperatorProjectManager$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementActions.assignOperatorProjectManager),
      switchMap(({ surveyId, projectManager }) =>
        this._service
          .assignOperatorProjectManager(surveyId, projectManager.id)
          .pipe(
            tap(() =>
              this._toast.success(
                'Success! Project Manager assigned successfully',
                { keepOpen: false },
              ),
            ),
            map(() =>
              SurveyManagementApiActions.assignOperatorProjectManagerSuccess({
                surveyId,
                projectManager,
              }),
            ),
            this.psCatchError(
              (error) =>
                of(
                  SurveyManagementApiActions.assignOperatorProjectManagerFailure(
                    {
                      error: error.message,
                    },
                  ),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public removeOperatorProjectManager$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementActions.removeOperatorProjectManager),
      switchMap(({ surveyId }) =>
        this._service.removeOperatorProjectManager(surveyId).pipe(
          tap(() =>
            this._toast.success(
              'Success! Project Manager removed successfully',
              { keepOpen: false },
            ),
          ),
          map(() =>
            SurveyManagementApiActions.removeOperatorProjectManagerSuccess({
              surveyId,
            }),
          ),
          this.psCatchError(
            (error) =>
              of(
                SurveyManagementApiActions.removeOperatorProjectManagerFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public patchSurveyOnBuyerProjectManagerAssigned$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementApiActions.assignBuyerProjectManagerSuccess),
      map(({ surveyId, projectManager }) =>
        ManageSurveyActions.updateSurvey({
          surveyId,
          survey: { buyerPm: projectManager.name },
        }),
      ),
    ),
  );

  public patchSurveyOnBuyerProjectManagerRemoved$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementApiActions.removeBuyerProjectManagerSuccess),
      map(({ surveyId }) =>
        ManageSurveyActions.updateSurvey({ surveyId, survey: { buyerPm: '' } }),
      ),
    ),
  );

  public patchSurveyOnOperatorProjectManagerAssigned$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementApiActions.assignOperatorProjectManagerSuccess),
      map(({ surveyId, projectManager }) =>
        ManageSurveyActions.updateSurvey({
          surveyId,
          survey: { operatorPm: projectManager.name },
        }),
      ),
    ),
  );

  public patchSurveyOnOperatorProjectManagerRemoved$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementApiActions.removeOperatorProjectManagerSuccess),
      map(({ surveyId }) =>
        ManageSurveyActions.updateSurvey({
          surveyId,
          survey: { operatorPm: '' },
        }),
      ),
    ),
  );

  // Serialized rather than switchMap: this is a write, not a lookup — a
  // second click before the first request settles must not cancel and lose
  // track of it.
  public createPriceBlock$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementActions.createPriceBlock),
      concatMap(({ surveyId }) =>
        this._fusionService.createPriceBlock(surveyId).pipe(
          tap((message) => this._toast.info(message, { keepOpen: false })),
          map((message) =>
            SurveyManagementApiActions.createPriceBlockSuccess({ message }),
          ),
          this.psCatchError(
            (err) =>
              of(
                SurveyManagementApiActions.createPriceBlockFailure({
                  error: err.message ?? 'Failed to create price block',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  // Same reasoning as createPriceBlock$ above — serialize instead of
  // switchMap so a rapid second click can't cancel an in-flight reset.
  public resetFusionBlockStats$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementActions.resetFusionBlockStats),
      concatMap(({ surveyId }) =>
        this._fusionService.resetFusionBlockStats(surveyId).pipe(
          tap((message) => this._toast.success(message, { keepOpen: false })),
          map((message) =>
            SurveyManagementApiActions.resetFusionBlockStatsSuccess({
              message,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                SurveyManagementApiActions.resetFusionBlockStatsFailure({
                  error: err.message ?? 'Failed to reset fusion block stats',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadFusionStats$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyManagementActions.loadFusionStats),
      switchMap(({ surveyId }) =>
        this._fusionService.getFusionStats(surveyId).pipe(
          map((stats) =>
            SurveyManagementApiActions.loadFusionStatsSuccess({ stats }),
          ),
          this.psCatchError(
            (err) =>
              of(
                SurveyManagementApiActions.loadFusionStatsFailure({
                  error: err.message ?? 'Failed to load fusion stats',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );
}
